import * as Application from "expo-application";
import * as Sentry from "@sentry/react-native";
import * as SecureStore from "expo-secure-store";
import { Linking } from "react-native";

import type {
  AttributionData,
  CampaignData,
} from "@/schemas/deeplinking/campaign";
import type { KeyValue } from "@/schemas/api/tracking";
import { isAndroid } from "@/utils/platform";

export const ATTRIBUTION_DATA_KEY = "attribution_data";

const EMPTY_CAMPAIGN: CampaignData = {
  campaign: null,
  source: null,
  medium: null,
  params: {},
};

export function parseDeepLinkUrl(url: string | null): CampaignData {
  if (!url) {
    return { ...EMPTY_CAMPAIGN };
  }
  const urlObj = new URL(url);

  const requiredParams = ["campaign", "source", "medium"];

  // Extract all URL parameters
  const allParams = Object.fromEntries(urlObj.searchParams.entries());

  const customParams = Object.fromEntries(
    Object.entries(allParams).filter(([key]) => !requiredParams.includes(key)),
  );

  return {
    campaign: urlObj.searchParams.get("campaign") || null,
    source: urlObj.searchParams.get("source") || null,
    medium: urlObj.searchParams.get("medium") || null,
    params: customParams,
  };
}

export function generateCampaignUrl(campaignData: CampaignData): string {
  const baseUrl = "kai://app";
  const params = new URLSearchParams();

  // Add required UTM parameters
  if (campaignData.campaign) {
    params.set("campaign", campaignData.campaign);
  }
  if (campaignData.source) {
    params.set("source", campaignData.source);
  }
  if (campaignData.medium) {
    params.set("medium", campaignData.medium);
  }

  if (campaignData.params) {
    // Add custom parameters
    Object.entries(campaignData.params).forEach(([key, value]) => {
      params.set(key, value);
    });
  }

  return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
}

export async function getAttributionDataFromStorage(): Promise<AttributionData | null> {
  const dataString = await SecureStore.getItemAsync(ATTRIBUTION_DATA_KEY);
  if (!dataString) {
    return null;
  }
  try {
    return JSON.parse(dataString) as AttributionData;
  } catch (error) {
    Sentry.captureException(error, {
      extra: { dataString },
    });
    return null;
  }
}

function campaignDataToKeyValues(
  data: CampaignData | null,
  prefix: string,
): KeyValue[] {
  const keyValues: KeyValue[] = [];

  keyValues.push({ key: `${prefix}.campaign`, value: data?.campaign || "" });
  keyValues.push({ key: `${prefix}.source`, value: data?.source || "" });
  keyValues.push({ key: `${prefix}.medium`, value: data?.medium || "" });

  Object.entries(data?.params || {}).forEach(([key, value]) => {
    keyValues.push({ key: `${prefix}.${key}`, value });
  });

  return keyValues;
}

export function convertAttributionDataToKeyValue(
  attribution: AttributionData | null,
): KeyValue[] {
  return [
    ...campaignDataToKeyValues(attribution?.deepLink ?? null, "DEEP_LINK"),
    ...campaignDataToKeyValues(
      attribution?.installReferrer ?? null,
      "INSTALL_REFERRER",
    ),
  ];
}

export function parseInstallReferrer(referrer: string): CampaignData {
  const params = new URLSearchParams(referrer);
  const utmKeys = ["utm_source", "utm_medium", "utm_campaign"];

  const customParams: Record<string, string> = {};
  params.forEach((value, key) => {
    if (!utmKeys.includes(key)) {
      customParams[key] = value;
    }
  });

  return {
    campaign: params.get("utm_campaign") || null,
    source: params.get("utm_source") || null,
    medium: params.get("utm_medium") || null,
    params: customParams,
  };
}

export async function processCampaignAttribution(
  url?: string | null,
): Promise<void> {
  const attribution: AttributionData = {
    deepLink: { ...EMPTY_CAMPAIGN },
    installReferrer: null,
  };

  // Collect deep link data
  try {
    const deepLinkUrl = url ?? (await Linking.getInitialURL());
    attribution.deepLink = parseDeepLinkUrl(deepLinkUrl);
  } catch (error) {
    Sentry.captureException(error, {
      tags: { type: "campaign_deep_link" },
    });
  }

  // Collect install referrer (Android only)
  if (isAndroid()) {
    try {
      const referrer = await Application.getInstallReferrerAsync();
      attribution.installReferrer = referrer
        ? parseInstallReferrer(referrer)
        : { ...EMPTY_CAMPAIGN };
    } catch (error) {
      Sentry.captureException(error, {
        tags: { type: "campaign_install_referrer" },
      });
    }
  }

  // Store everything in one write
  try {
    await SecureStore.setItemAsync(
      ATTRIBUTION_DATA_KEY,
      JSON.stringify(attribution),
    );
  } catch (error) {
    Sentry.captureException(error, {
      tags: { type: "campaign_attribution_storage" },
    });
  }
}
